import { useEffect, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import type { CandidateNote } from '../../types/taActionBoard';
import { fetchCandidateNotes } from '../../services/taCandidateNotesApi';
import { COLORS, fmtDate } from './format';

/**
 * A candidate's Lever notes, with a box to add another. Opened from the 📝 chip, which is on every
 * card whether or not there are notes yet — so this opens on an empty list as often as a full one,
 * and says so rather than showing a gap above the box. Adding posts to Lever as the signed-in
 * recruiter and appends here immediately. Internal notes only — never candidate-facing text.
 *
 * ⚠️ **The notes are READ when this opens**, not carried on the board payload (Theresa, Oct 2026).
 * They used to cost one Lever call per candidate in the pipeline on every build — a third of the
 * board's entire per-candidate traffic — to populate a count on a chip. Three states follow from
 * that and each has to look different: still loading, read and empty, and could-not-read. An
 * empty list is the one a reader will believe, so it must never stand in for the other two.
 */
const MAX = 4000; // mirrors MAX_NOTE_CHARS in proxy lib/candidateNotes.js

export function NotesModal({
  candidateName,
  opportunityId,
  onClose,
  onAdd,
}: {
  candidateName: string;
  /** Whose notes to read. The modal fetches them itself — see the note above. */
  opportunityId: string;
  onClose: () => void;
  /** Posts the note to Lever and resolves with the created note. */
  onAdd: (value: string) => Promise<CandidateNote>;
}) {
  const [list, setList] = useState<CandidateNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One read per mount: the modal is mounted against a chosen candidate and unmounted on close,
  // so `opportunityId` cannot change under it. Nothing is reset synchronously here on purpose —
  // the initial state IS the loading state, and a synchronous setState in an effect costs a
  // cascading render for nothing (and lint rightly refuses it).
  useEffect(() => {
    let live = true;
    fetchCandidateNotes(opportunityId)
      .then(({ notes, notesForbidden, unread }) => {
        if (!live) return;
        setList(notes);
        // Neither of these may be allowed to render as "no notes on this candidate yet" — that
        // sentence is a claim, and after a throttle or a missing permission it is a false one.
        // Adding a note still works in both cases; this route is the read only.
        if (notesForbidden) {
          setLoadError(
            'The Lever API key cannot read notes at the moment, so existing ones are not shown. ' +
              'Adding a note still works.'
          );
        } else if (unread) {
          setLoadError(
            'Lever would not hand over this candidate’s notes just now, so any existing ones are ' +
              'not shown. Close and reopen to try again — adding a note still works.'
          );
        }
      })
      .catch((e) => {
        if (!live) return;
        setLoadError(e instanceof Error ? e.message : 'Could not read the notes from Lever.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [opportunityId]);

  const save = async () => {
    const text = value.trim();
    if (!text || saving) return;
    setSaving(true);
    setError(null);
    try {
      const created = await onAdd(text);
      setList((prev) => [...prev, created]);
      setValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,36rem)] gap-5 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle>📝 Notes — {candidateName}</ModalTitle>
        </ModalHeader>

        <div className="space-y-3">
          {/* Three distinct states. "No notes yet" is a claim, and it must not be made while we
              are still reading or after a read that failed. */}
          {loading && <p className="text-sm text-gray-400">Reading the notes from Lever…</p>}
          {loadError && <p className="text-sm text-amber-700">{loadError}</p>}
          {!loading && !loadError && list.length === 0 && (
            <p className="text-sm text-gray-500">
              No notes on this candidate yet — yours will be the first.
            </p>
          )}
          {list.map((note) => (
            <div key={note.id} className="rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
              <div className="mb-1 text-xs text-gray-500">
                {note.author || 'FINN'}
                {note.createdAt ? ` · ${fmtDate(note.createdAt)}` : ''}
              </div>
              {note.text ? (
                <div className="whitespace-pre-wrap text-sm text-gray-900">{note.text}</div>
              ) : (
                <div className="text-sm italic text-gray-400">
                  No preview available — open the candidate in Lever to read this note.
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-600">Add a note</div>
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Add an internal note — it syncs to the candidate's Lever profile…"
            rows={3}
            maxLength={MAX}
          />
          <p className="text-xs text-gray-400">
            Syncs to Lever as your note. Internal only — don't paste candidate-facing text here.
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button onClick={save} disabled={!value.trim() || saving}>
            {saving ? 'Saving…' : 'Save to Lever'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
